import type { Campaign, CampaignInstrument, CampaignSurveyTypeTag } from '../types'
import {
  DYNAMIC_SURVEY_LABEL,
  launchSurveyTypeLabel,
  REGULAR_SURVEY_LABEL,
} from './launchSurveyMetadata'

export type CampaignListInstrumentMetrics = {
  instrumentCount: number
  totalInstrumentResponses: number
  budgetUsed: number
  /** Sum of responseCount × rewardPerResponse across instruments (distributed rewards). */
  rewardsDistributedUsd: number
  hasLiveInstrument: boolean
  allInstrumentsCompleted: boolean
  /** Unique Regular / Dynamic labels for the campaign tile. Empty when the campaign has no surveys. */
  surveyTypeTags: CampaignSurveyTypeTag[]
}

function surveyTypeTagsFromInstruments(
  instruments: CampaignInstrument[],
): CampaignSurveyTypeTag[] {
  if (instruments.length === 0) return []

  let hasRegular = false
  let hasDynamic = false
  for (const row of instruments) {
    if (launchSurveyTypeLabel(row.audienceAvailability) === DYNAMIC_SURVEY_LABEL) {
      hasDynamic = true
    } else {
      hasRegular = true
    }
  }

  const tags: CampaignSurveyTypeTag[] = []
  if (hasRegular) tags.push(REGULAR_SURVEY_LABEL)
  if (hasDynamic) tags.push(DYNAMIC_SURVEY_LABEL)
  return tags
}

function rewardsDistributedUsdFromInstrument(row: CampaignInstrument): number {
  const reward = row.rewardPerResponse
  const count = row.responseCount ?? 0
  if (reward == null || !Number.isFinite(reward) || reward <= 0 || count <= 0) {
    return 0
  }
  return count * reward
}

function isLiveInstrumentStatus(status: CampaignInstrument['status']): boolean {
  return status === 'active' || status === 'paused'
}

export function metricsFromInstruments(
  instruments: CampaignInstrument[],
): CampaignListInstrumentMetrics {
  const hasInstruments = instruments.length > 0
  return {
    instrumentCount: instruments.length,
    totalInstrumentResponses: instruments.reduce(
      (sum, row) => sum + (row.responseCount ?? 0),
      0,
    ),
    budgetUsed: instruments.reduce(
      (sum, row) => sum + (row.allocatedBudgetUsd ?? 0),
      0,
    ),
    rewardsDistributedUsd: instruments.reduce(
      (sum, row) => sum + rewardsDistributedUsdFromInstrument(row),
      0,
    ),
    hasLiveInstrument: instruments.some((row) => isLiveInstrumentStatus(row.status)),
    allInstrumentsCompleted:
      hasInstruments && instruments.every((row) => row.status === 'closed'),
    surveyTypeTags: surveyTypeTagsFromInstruments(instruments),
  }
}

/** Prefer instrument-derived aggregates when the list API has not enriched the row yet. */
export function mergeCampaignWithInstrumentMetrics(
  campaign: Campaign,
  metrics: CampaignListInstrumentMetrics,
): Campaign {
  return {
    ...campaign,
    instrumentCount: metrics.instrumentCount,
    totalInstrumentResponses: metrics.totalInstrumentResponses,
    budgetUsed: metrics.budgetUsed,
    allInstrumentsCompleted: metrics.allInstrumentsCompleted,
    surveyTypeTags: metrics.surveyTypeTags,
  }
}

export function enrichCampaignsWithInstrumentMetrics(
  campaigns: Campaign[],
  metricsByCampaignId: ReadonlyMap<string, CampaignListInstrumentMetrics>,
): Campaign[] {
  return campaigns.map((campaign) => {
    const metrics = metricsByCampaignId.get(campaign.id)
    if (!metrics) return campaign
    return mergeCampaignWithInstrumentMetrics(campaign, metrics)
  })
}

export function sumTotalRewardsDistributedUsd(
  metricsByCampaignId: ReadonlyMap<string, CampaignListInstrumentMetrics>,
): number {
  let total = 0
  for (const metrics of metricsByCampaignId.values()) {
    total += metrics.rewardsDistributedUsd
  }
  return total
}
